@file:OptIn(ExperimentalUuidApi::class)

package ai.wagemix.data

import com.fsryan.foundation.data.FSSyncEvent
import kotlin.uuid.ExperimentalUuidApi
import kotlin.uuid.Uuid

/**
 * The authentication operations. No event carries a credential; the signed-in user lands in [WMCache].
 *
 * One nested sealed triple per operation (sync data source playbook, Step 2). Every event carries
 * the operation's inputs and nothing it produced: outcomes are written to a local data source and
 * observed there. `Failure` adds the error. Constructors are public because the backend variant
 * module constructs these events (controlled-construction playbook: sync-event lifecycle variants).
 */
sealed interface WMAuthSyncEvent<out E: WMAuthSyncEvent<E>>: FSSyncEvent<E> {

    /** Email and password sign-in. */
    sealed interface SignIn<out E: SignIn<E>>: WMAuthSyncEvent<E> {
        val email: String

        data class Initial(
            override val email: String,
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): SignIn<Initial>, FSSyncEvent.Initial<Initial> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        data class Success(
            override val email: String,
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): SignIn<Success>, FSSyncEvent.Success<Success> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        data class Failure(
            override val email: String,
            override val error: Throwable,
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): SignIn<Failure>, FSSyncEvent.Error<Failure> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }
    }

    /** Account creation with the profile name. */
    sealed interface Register<out E: Register<E>>: WMAuthSyncEvent<E> {
        val email: String

        data class Initial(
            override val email: String,
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): Register<Initial>, FSSyncEvent.Initial<Initial> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        data class Success(
            override val email: String,
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): Register<Success>, FSSyncEvent.Success<Success> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        data class Failure(
            override val email: String,
            override val error: Throwable,
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): Register<Failure>, FSSyncEvent.Error<Failure> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }
    }

    /** Send the verification email to the signed-in user. */
    sealed interface SendEmailVerification<out E: SendEmailVerification<E>>: WMAuthSyncEvent<E> {
        data class Initial(
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): SendEmailVerification<Initial>, FSSyncEvent.Initial<Initial> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        data class Success(
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): SendEmailVerification<Success>, FSSyncEvent.Success<Success> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        data class Failure(
            override val error: Throwable,
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): SendEmailVerification<Failure>, FSSyncEvent.Error<Failure> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }
    }

    /** Send the password-reset email. */
    sealed interface SendPasswordReset<out E: SendPasswordReset<E>>: WMAuthSyncEvent<E> {
        val email: String

        data class Initial(
            override val email: String,
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): SendPasswordReset<Initial>, FSSyncEvent.Initial<Initial> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        data class Success(
            override val email: String,
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): SendPasswordReset<Success>, FSSyncEvent.Success<Success> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        data class Failure(
            override val email: String,
            override val error: Throwable,
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): SendPasswordReset<Failure>, FSSyncEvent.Error<Failure> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }
    }

    /** Check a reset code; the account's email lands in the global local context under [code]. */
    sealed interface VerifyPasswordResetCode<out E: VerifyPasswordResetCode<E>>: WMAuthSyncEvent<E> {
        val code: String

        data class Initial(
            override val code: String,
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): VerifyPasswordResetCode<Initial>, FSSyncEvent.Initial<Initial> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        data class Success(
            override val code: String,
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): VerifyPasswordResetCode<Success>, FSSyncEvent.Success<Success> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        data class Failure(
            override val code: String,
            override val error: Throwable,
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): VerifyPasswordResetCode<Failure>, FSSyncEvent.Error<Failure> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }
    }

    /** Set a new password with a verified reset code. */
    sealed interface ConfirmPasswordReset<out E: ConfirmPasswordReset<E>>: WMAuthSyncEvent<E> {
        val code: String

        data class Initial(
            override val code: String,
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): ConfirmPasswordReset<Initial>, FSSyncEvent.Initial<Initial> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        data class Success(
            override val code: String,
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): ConfirmPasswordReset<Success>, FSSyncEvent.Success<Success> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        data class Failure(
            override val code: String,
            override val error: Throwable,
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): ConfirmPasswordReset<Failure>, FSSyncEvent.Error<Failure> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }
    }

    /** Apply an email action code (verification). */
    sealed interface ApplyActionCode<out E: ApplyActionCode<E>>: WMAuthSyncEvent<E> {
        val code: String

        data class Initial(
            override val code: String,
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): ApplyActionCode<Initial>, FSSyncEvent.Initial<Initial> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        data class Success(
            override val code: String,
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): ApplyActionCode<Success>, FSSyncEvent.Success<Success> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        data class Failure(
            override val code: String,
            override val error: Throwable,
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): ApplyActionCode<Failure>, FSSyncEvent.Error<Failure> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }
    }

    /** Sign out; the active user is cleared from [WMCache]. */
    sealed interface SignOut<out E: SignOut<E>>: WMAuthSyncEvent<E> {
        data class Initial(
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): SignOut<Initial>, FSSyncEvent.Initial<Initial> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        data class Success(
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): SignOut<Success>, FSSyncEvent.Success<Success> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        data class Failure(
            override val error: Throwable,
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): SignOut<Failure>, FSSyncEvent.Error<Failure> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }
    }

    /** Change the signed-in user's first and last name. */
    sealed interface UpdateProfile<out E: UpdateProfile<E>>: WMAuthSyncEvent<E> {
        val uid: String

        data class Initial(
            override val uid: String,
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): UpdateProfile<Initial>, FSSyncEvent.Initial<Initial> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        data class Success(
            override val uid: String,
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): UpdateProfile<Success>, FSSyncEvent.Success<Success> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        data class Failure(
            override val uid: String,
            override val error: Throwable,
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): UpdateProfile<Failure>, FSSyncEvent.Error<Failure> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }
    }
}
